import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "~/components/ui/button";

interface ScrollableTableWrapperProps {
  children: ReactNode;
}

export default function ScrollableTableWrapper({
  children,
}: ScrollableTableWrapperProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      observer.disconnect();
    };
  }, [checkScroll]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === "left" ? -400 : 400,
      behavior: "smooth",
    });
  };

  return (
    <div className="relative w-full min-w-0 rounded-lg border bg-card">
      {canScrollLeft && (
        <Button
          variant="outline"
          size="icon"
          className="absolute left-1 top-1/2 z-30 h-8 w-8 -translate-y-1/2 rounded-full bg-background/90 shadow-md"
          onClick={() => scroll("left")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      )}
      {canScrollRight && (
        <Button
          variant="outline"
          size="icon"
          className="absolute right-1 top-1/2 z-30 h-8 w-8 -translate-y-1/2 rounded-full bg-background/90 shadow-md"
          onClick={() => scroll("right")}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}
      <div
        ref={scrollRef}
        className="max-h-[75vh] w-full overflow-x-auto overflow-y-auto"
      >
        <table className="w-full caption-bottom text-sm [&_tfoot]:sticky [&_tfoot]:bottom-0 [&_tfoot]:z-20 [&_tfoot_td]:bg-card [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-20 [&_thead_th]:bg-card">
          {children}
        </table>
      </div>
    </div>
  );
}
